import { create } from 'zustand'
import type { Message } from '../types/chat.types'
import type { AttachedFile } from '../types/chatCache.types'

export interface Conversation {
    id: string
    title: string
    agent_id: string | null
    created_at: string
    updated_at: string
}

/**
 * Unified Agent Lifecycle States (Phase 4.2)
 * Eliminates race conditions between multiple loading/sending booleans.
 */
export type AgentLifeCycleState =
    | 'idle'           // Ready for input
    | 'loading'        // Initial boot or switching context
    | 'thinking'       // Request sent, waiting for first token
    | 'streaming'      // Tokens are arriving
    | 'error'          // Global error state

export interface AgentState {
    id: string
    name: string

    // Data
    messages: Message[]
    conversations: Conversation[]
    currentConversationId: string | null
    attachedFiles: AttachedFile[]

    // Load Status Flags (Production-Grade)
    conversationsLoaded: boolean
    messagesLoadedForConversationId: string | null
    hasAttemptedMessageLoad: boolean
    messageLoadError: Error | null
    lastMessageLoadTimestamp: number | null

    // UI State (Consolidated Phase 4.2)
    lifecycle: AgentLifeCycleState
    inputMessage: string
    isLoadingMessages: boolean
    isLoadingConversations: boolean

    // Streaming
    streamingMessage: {
        id: string
        role: 'assistant'
        content: string
        isStreaming: boolean
        timestamp: string
    } | null

    // Streaming Diagnostics (Phase 3)
    connectionStatus: 'connecting' | 'connected' | 'error' | 'idle'
    lastStreamError: string | null

    // Metadata
    lastAccessed: number // For LRU Eviction Policy (Phase 6)
    lastUpdated: number
}

interface ChatStore {
    // State
    agents: Record<string, AgentState>
    activeAgentId: string | null

    // Actions - Global
    setActiveAgent: (agentId: string) => void

    // Actions - Per Agent
    initAgent: (agentId: string, name: string) => void
    setMessages: (agentId: string, messages: Message[], conversationId: string) => void
    addMessage: (agentId: string, message: Message) => void
    updateMessage: (agentId: string, messageId: string, updates: Partial<Message>) => void
    removeMessage: (agentId: string, messageId: string) => void
    clearMessages: (agentId: string) => void

    setConversations: (agentId: string, conversations: Conversation[]) => void
    setCurrentConversation: (agentId: string, conversationId: string | null) => void

    setAttachedFiles: (agentId: string, files: AttachedFile[]) => void
    addAttachedFile: (agentId: string, file: AttachedFile) => void
    removeAttachedFile: (agentId: string, fileId: string) => void
    clearAttachedFiles: (agentId: string) => void

    setLoadingMessages: (agentId: string, loading: boolean) => void
    setLoadingConversations: (agentId: string, loading: boolean) => void
    setLifeCycleState: (agentId: string, state: AgentLifeCycleState) => void
    setStreamingMessage: (agentId: string, message: AgentState['streamingMessage']) => void

    // Production-Grade Actions
    setMessageLoadAttempted: (agentId: string, conversationId: string) => void
    setMessageLoadError: (agentId: string, error: Error | null) => void
    clearAgentState: (agentId: string) => void

    // Streaming Resilience Actions (Phase 3)
    setConnectionStatus: (agentId: string, status: AgentState['connectionStatus'], error?: string | null) => void
    markMessageAsFailed: (agentId: string, messageId: string) => void

    // Outbox Pattern (Phase 2)
    pendingMessages: PendingMessage[]
    addToOutbox: (message: PendingMessage) => void
    removeFromOutbox: (id: string) => void
    updateOutboxMessage: (id: string, updates: Partial<PendingMessage>) => void

    // Selectors
    getActiveAgentState: () => AgentState | undefined
}

// Outbox Types
export interface PendingMessage {
    id: string
    agentId: string
    conversationId: string
    content: string
    files: AttachedFile[]
    retryCount: number
    lastAttempt: number
    status: 'pending' | 'sending' | 'failed'
}

// Configuration
const MAX_CACHED_AGENTS = 5

export const useChatStore = create<ChatStore>()((set, get) => ({
    agents: {},
    activeAgentId: null,

    setActiveAgent: (agentId) => set({ activeAgentId: agentId }),

    initAgent: (id, name) => set((state) => {
        const agents = state.agents
        if (agents[id]) {
            // Update access time for LRU
            return {
                agents: {
                    ...agents,
                    [id]: {
                        ...agents[id],
                        lastAccessed: Date.now()
                    }
                }
            }
        }

        // If not present, create new and check for eviction
        const agentKeys = Object.keys(agents)
        let newAgents = { ...agents }

        // LRU Eviction Logic (Phase 6)
        if (agentKeys.length >= MAX_CACHED_AGENTS) {
            // Find the oldest agent (minimum lastAccessed)
            const oldestId = agentKeys.reduce((oldest, currentId) => {
                const oldestTime = agents[oldest]?.lastAccessed || 0
                const currentTime = agents[currentId]?.lastAccessed || 0
                return currentTime < oldestTime ? currentId : oldest
            }, agentKeys[0])

            if (oldestId) {
                console.log('[LRU-Eviction] Evicting oldest agent:', oldestId)
                delete newAgents[oldestId]
            }
        }

        return {
            agents: {
                ...newAgents,
                [id]: {
                    id,
                    name,
                    messages: [],
                    conversations: [],
                    currentConversationId: null,
                    attachedFiles: [],
                    conversationsLoaded: false,
                    messagesLoadedForConversationId: null,
                    hasAttemptedMessageLoad: false,
                    messageLoadError: null,
                    lastMessageLoadTimestamp: null,
                    inputMessage: '',
                    lifecycle: 'idle',
                    isLoadingMessages: false,
                    isLoadingConversations: false,
                    streamingMessage: null,
                    connectionStatus: 'idle',
                    lastStreamError: null,
                    lastAccessed: Date.now(),
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    setMessages: (agentId, messages, conversationId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages,
                    messagesLoadedForConversationId: conversationId,
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    addMessage: (agentId, message) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages: [...agent.messages, message],
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    updateMessage: (agentId, messageId, updates) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages: agent.messages.map(m => m.id === messageId ? { ...m, ...updates } : m)
                }
            }
        }
    }),

    removeMessage: (agentId, messageId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages: agent.messages.filter(m => m.id !== messageId)
                }
            }
        }
    }),

    clearMessages: (agentId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages: [],
                    messagesLoadedForConversationId: null
                }
            }
        }
    }),

    setConversations: (agentId, conversations) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    conversations,
                    conversationsLoaded: true, // Mark as loaded
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    setCurrentConversation: (agentId, conversationId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        // When switching conversation, we might want to clear messagesLoaded flag if needed
        // but typically hooks will detect mismatch and reload. 
        // Better to Keep messages if they match, but if ID changes, hooks handle it.
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, currentConversationId: conversationId }
            }
        }
    }),

    setAttachedFiles: (agentId, files) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, attachedFiles: files }
            }
        }
    }),

    addAttachedFile: (agentId, file) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, attachedFiles: [...agent.attachedFiles, file] }
            }
        }
    }),

    removeAttachedFile: (agentId, fileId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, attachedFiles: agent.attachedFiles.filter(f => f.id !== fileId) }
            }
        }
    }),

    clearAttachedFiles: (agentId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, attachedFiles: [] }
            }
        }
    }),

    setLoadingMessages: (agentId, loading) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, isLoadingMessages: loading }
            }
        }
    }),

    setLoadingConversations: (agentId, loading) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, isLoadingConversations: loading }
            }
        }
    }),

    setLifeCycleState: (agentId, lifecycle) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, lifecycle }
            }
        }
    }),

    setStreamingMessage: (agentId, message) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: { ...agent, streamingMessage: message }
            }
        }
    }),

    setMessageLoadAttempted: (agentId, conversationId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    hasAttemptedMessageLoad: true,
                    messagesLoadedForConversationId: conversationId,
                    lastMessageLoadTimestamp: Date.now(),
                    messageLoadError: null
                }
            }
        }
    }),

    setMessageLoadError: (agentId, error) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messageLoadError: error,
                    hasAttemptedMessageLoad: true
                }
            }
        }
    }),

    clearAgentState: (agentId) => set((state) => {
        const newAgents = { ...state.agents }
        delete newAgents[agentId]
        return {
            agents: newAgents,
            activeAgentId: state.activeAgentId === agentId ? null : state.activeAgentId
        }
    }),

    setConnectionStatus: (agentId, status, error = null) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    connectionStatus: status,
                    lastStreamError: error,
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    markMessageAsFailed: (agentId, messageId) => set((state) => {
        const agent = state.agents[agentId]
        if (!agent) return state
        return {
            agents: {
                ...state.agents,
                [agentId]: {
                    ...agent,
                    messages: agent.messages.map(m =>
                        m.id === messageId ? { ...m, status: 'error' as any } : m
                    ),
                    isSending: false,
                    connectionStatus: 'error',
                    lastUpdated: Date.now()
                }
            }
        }
    }),

    // Outbox Implementation
    pendingMessages: [],

    addToOutbox: (message) => set((state) => ({
        pendingMessages: [...state.pendingMessages, message]
    })),

    removeFromOutbox: (id) => set((state) => ({
        pendingMessages: state.pendingMessages.filter(m => m.id !== id)
    })),

    updateOutboxMessage: (id, updates) => set((state) => ({
        pendingMessages: state.pendingMessages.map(m =>
            m.id === id ? { ...m, ...updates } : m
        )
    })),

    getActiveAgentState: () => {
        const state = get()
        if (!state.activeAgentId) return undefined
        return state.agents[state.activeAgentId]
    }
}))
